import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useState } from 'react'
import {
  offeneVorschlaegeQuery,
  verworfenLesen,
  verworfenMerken,
  type OffenerVorschlag,
  type Tageslage,
} from '../../lib/mywork'
import { trpc } from '../../lib/trpc'

/**
 * **Offene Enden** ([Z-24](../../../../../docs/phasen/zusatz-24-my-work.md) AP 24.26) – alles, was
 * eine Schleife offen lässt, an einer Stelle: „Aufgaben für dich" aus Notizen und Protokollen der
 * letzten 7 Tage, die noch nicht in Work stehen, Zettel ohne Mappe, Aufnahmen ohne Termin.
 *
 * „Übernehmen" geht über `notiz.aufgabeAnlegen` – derselbe Weg wie im Blatt (E-432: nichts von
 * selbst). Erst wenn hier nichts mehr steht, ist die Karte weg.
 */
export function OffeneEnden({
  lage,
  kompakt = false,
}: {
  lage: Pick<Tageslage, 'zettelOhneMappe' | 'aufnahmenOhneTermin'> | undefined
  /** Im Feierabend: ohne Rahmen und Überschrift. */
  kompakt?: boolean
}) {
  const { data: vorschlaege = [] } = useQuery(offeneVorschlaegeQuery)
  const qc = useQueryClient()
  const [weg, setWeg] = useState<Set<string>>(() => verworfenLesen())
  const [hinweis, setHinweis] = useState<string | null>(null)
  /** Aufgeklappte Notizen – zu Beginn keine; eine Besprechung kann ein Dutzend Vorschläge bringen. */
  const [offen, setOffen] = useState<Set<string>>(new Set())
  const [alleGruppen, setAlleGruppen] = useState(false)
  const [aufnahmenOffen, setAufnahmenOffen] = useState(false)
  const schluessel = (v: OffenerVorschlag) => `${v.notizId}:${v.text}`
  const sichtbar = vorschlaege.filter((v) => !weg.has(schluessel(v)))
  const zettel = lage?.zettelOhneMappe ?? 0
  const aufnahmen = lage?.aufnahmenOhneTermin ?? []
  /** Je Notiz eine Gruppe, die jüngste zuerst (die Liste kommt schon so sortiert). */
  const gruppen = [...new Set(sichtbar.map((v) => v.notizId))].map((notizId) => {
    const vorschlaege = sichtbar.filter((v) => v.notizId === notizId)
    const erster = vorschlaege[0]!
    return {
      notizId,
      notizbuchId: erster.notizbuchId,
      titel: erster.termin ?? erster.notizTitel,
      datum: new Date(erster.datum).toLocaleDateString('de-DE', {
        weekday: 'short',
        day: 'numeric',
        month: 'numeric',
      }),
      vorschlaege,
    }
  })
  const anzahl = sichtbar.length + zettel + aufnahmen.length
  if (anzahl === 0 && !kompakt) return null

  const verwerfen = (v: OffenerVorschlag) => {
    verworfenMerken(schluessel(v))
    setWeg((alt) => new Set([...alt, schluessel(v)]))
  }
  const uebernehmen = async (v: OffenerVorschlag) => {
    setWeg((alt) => new Set([...alt, schluessel(v)]))
    try {
      await trpc.notizbuch.notiz.aufgabeAnlegen.mutate({
        notizbuchId: v.notizbuchId,
        id: v.notizId,
        text: v.text,
        zitat: v.zitat || null,
        frist: v.frist,
        ...(v.schritte.length ? { schritte: v.schritte } : {}),
      })
      setHinweis(`In Work: ${v.text}`)
      void qc.invalidateQueries({ queryKey: ['mywork'] })
    } catch (e) {
      setWeg((alt) => {
        const neu = new Set(alt)
        neu.delete(schluessel(v))
        return neu
      })
      setHinweis(`Nicht übernommen: ${e instanceof Error ? e.message : 'Fehler'}`)
    }
  }

  const inhalt = (
    <div className="flex flex-col gap-2.5 text-[13px]">
      {anzahl === 0 && <p className="text-ink2">Nichts offen – alles hat einen Platz.</p>}
      {(alleGruppen ? gruppen : gruppen.slice(0, 4)).map((g) => {
        const auf = offen.has(g.notizId)
        return (
          <div key={g.notizId} className="rounded-[10px] bg-[#fffbf2] px-2.5 py-2">
            <button
              type="button"
              aria-expanded={auf}
              onClick={() =>
                setOffen((alt) => {
                  const neu = new Set(alt)
                  if (neu.has(g.notizId)) neu.delete(g.notizId)
                  else neu.add(g.notizId)
                  return neu
                })
              }
              className="flex w-full cursor-pointer items-start gap-2 text-left"
            >
              <span aria-hidden="true" className="text-muted">
                {auf ? '▾' : '▸'}
              </span>
              <span className="min-w-0 flex-1">
                <span className="line-clamp-2 font-semibold">{g.titel}</span>
                <span className="text-[12px] text-ink2">
                  {g.vorschlaege.length} {g.vorschlaege.length === 1 ? 'Aufgabe' : 'Aufgaben'} für
                  dich · {g.datum}
                </span>
              </span>
            </button>
            {auf && (
              <div className="mt-2 flex flex-col gap-2 pl-4">
                {g.vorschlaege.map((v) => (
                  <div
                    key={schluessel(v)}
                    className="flex flex-col gap-1 border-t border-[#f6efe0] pt-2"
                  >
                    <span>{v.text}</span>
                    <span className="flex gap-1.5">
                      <button
                        type="button"
                        onClick={() => void uebernehmen(v)}
                        className="h-[28px] cursor-pointer rounded-lg bg-[#f3f2ef] px-2.5 text-[12.5px] font-semibold"
                      >
                        Übernehmen
                      </button>
                      <button
                        type="button"
                        onClick={() => verwerfen(v)}
                        aria-label={`Verwerfen: ${v.text}`}
                        className="h-[28px] cursor-pointer rounded-lg px-2 text-[12.5px] text-muted hover:bg-[#f3f2ef]"
                      >
                        verwerfen
                      </button>
                    </span>
                  </div>
                ))}
                <div className="flex gap-3 border-t border-[#f6efe0] pt-2 text-[12px]">
                  <a
                    href={`/notizbuch#notiz-${g.notizbuchId}-${g.notizId}`}
                    className="font-semibold text-accent-deep"
                  >
                    Notiz öffnen
                  </a>
                  <button
                    type="button"
                    onClick={() => g.vorschlaege.forEach(verwerfen)}
                    className="cursor-pointer text-muted hover:text-ink2"
                  >
                    alle verwerfen
                  </button>
                </div>
              </div>
            )}
          </div>
        )
      })}
      {gruppen.length > 4 && (
        <button
          type="button"
          onClick={() => setAlleGruppen((x) => !x)}
          className="cursor-pointer text-left text-[12.5px] font-semibold text-accent-deep"
        >
          {alleGruppen ? 'weniger zeigen' : `+ ${gruppen.length - 4} weitere Notizen`}
        </button>
      )}
      {zettel > 0 && (
        <div className="flex items-center gap-2 border-t border-[#f6efe0] pt-2">
          <span className="flex-1">
            <b>{zettel} Zettel</b> <span className="text-ink2">ohne Mappe</span>
          </span>
          <Link
            to={'/notizbuch' as string}
            className="rounded-lg bg-[#f3f2ef] px-2.5 py-1 text-[12.5px] font-semibold"
          >
            ablegen
          </Link>
        </div>
      )}
      {aufnahmen.length > 0 && (
        <div className="border-t border-[#f6efe0] pt-2">
          <button
            type="button"
            aria-expanded={aufnahmenOffen}
            onClick={() => setAufnahmenOffen((x) => !x)}
            className="flex w-full cursor-pointer items-center gap-2 text-left"
          >
            <span aria-hidden="true" className="text-muted">
              {aufnahmenOffen ? '▾' : '▸'}
            </span>
            <span className="flex-1">
              <b>
                {aufnahmen.length} {aufnahmen.length === 1 ? 'Aufnahme' : 'Aufnahmen'}
              </b>{' '}
              <span className="text-ink2">ohne Termin</span>
            </span>
          </button>
          {aufnahmenOffen && (
            <ul className="mt-1.5 flex flex-col gap-1.5 pl-4">
              {aufnahmen.map((a) => (
                <li key={a.notizId} className="flex items-center gap-2">
                  <span className="line-clamp-2 min-w-0 flex-1 text-ink2">{a.titel}</span>
                  <a
                    href={`/notizbuch#notiz-${a.notizbuchId}-${a.notizId}`}
                    className="shrink-0 rounded-lg bg-[#f3f2ef] px-2.5 py-1 text-[12.5px] font-semibold"
                  >
                    zuordnen
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {hinweis && (
        <p role="status" className="text-[12px] text-ink2">
          {hinweis}
        </p>
      )}
    </div>
  )

  if (kompakt) return inhalt
  return (
    <section
      aria-label="Offene Enden"
      className="rounded-card border border-[#f1d39a] bg-surface px-3.5 py-3 shadow-task"
    >
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <h2 className="text-[10.5px] font-bold tracking-[0.08em] text-[#8a5300] uppercase">
          Offene Enden · {anzahl}
        </h2>
        <span className="text-[11.5px] text-muted">schließen, bevor sie vergessen werden</span>
      </div>
      {inhalt}
    </section>
  )
}
